import { Auction, AuctionRequest, FileParams, Patient } from "core/types";
import FileUploadStatusPanel from "dsl/ecosystems/PatientFiles/FileUploadStatusPanel";
import { useFilesFeatures } from "dsl/ecosystems/PatientFiles/useFilesFeature";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

type PatientFilesContextType = Omit<
  ReturnType<typeof useFilesFeatures>,
  "setUploadStates" | "uploadStates"
> & {
  setAuction: Dispatch<SetStateAction<Auction | undefined>>;
  setAuctionRequest: Dispatch<SetStateAction<AuctionRequest | undefined>>;
  setExternalId: Dispatch<SetStateAction<string | undefined>>;
  setParams: Dispatch<SetStateAction<FileParams | undefined>>;
  setDisableFileShare: Dispatch<SetStateAction<boolean>>;
};

const PatientFilesContext = createContext<PatientFilesContextType | undefined>(
  undefined,
);

export function PatientFilesProvider({ children }: { children: ReactNode }) {
  const [params, setParams] = useState<FileParams>();
  const [externalId, setExternalId] = useState<Patient["external_id"]>();
  const [auctionRequest, setAuctionRequest] = useState<AuctionRequest>();
  const [disableFileShare, setDisableFileShare] = useState<boolean>(false);
  // Store auction separately because PatientFiles page passes it directly,
  // while MessengerFiles passes auctionRequest. We store it here to normalize both contexts.
  const [auction, setAuction] = useState<Auction>();
  const { setUploadStates, uploadStates, ...filesFeature } = useFilesFeatures({
    auction,
    auctionRequest,
    disableFileShare,
    externalId: externalId,
    params: params as FileParams,
  });

  const memoizedSetAuction = useCallback(setAuction, []);
  const memoizedSetAuctionRequest = useCallback(setAuctionRequest, []);
  const memoizedSetDisableFileShare = useCallback(setDisableFileShare, []);
  const memoizedSetExternalId = useCallback(setExternalId, []);
  const memoizedSetParams = useCallback(setParams, []);

  return (
    <PatientFilesContext.Provider
      value={{
        ...filesFeature,
        setAuction: memoizedSetAuction,
        setAuctionRequest: memoizedSetAuctionRequest,
        setDisableFileShare: memoizedSetDisableFileShare,
        setExternalId: memoizedSetExternalId,
        setParams: memoizedSetParams,
      }}
    >
      {children}
      <FileUploadStatusPanel
        fileUploadStates={uploadStates}
        setFileUploadStates={setUploadStates}
      />
    </PatientFilesContext.Provider>
  );
}

export function usePatientFilesContext({
  auction,
  auctionRequest,
  disableFileShare,
  externalId,
  params,
}: {
  auction?: Auction;
  auctionRequest?: AuctionRequest;
  disableFileShare?: boolean;
  externalId: Patient["external_id"];
  params: FileParams;
}) {
  const context = useContext(PatientFilesContext);
  if (!context) {
    throw new Error(
      "usePatientFilesContext must be used within a PatientFilesProvider",
    );
  }

  const {
    setAuction,
    setAuctionRequest,
    setDisableFileShare,
    setExternalId,
    setParams,
  } = context;

  useEffect(() => {
    setAuction(auction);
    setAuctionRequest(auctionRequest);
    setParams(params);
    setExternalId(externalId);
    setDisableFileShare(disableFileShare ?? false);
  }, [
    auction,
    auctionRequest,
    disableFileShare,
    externalId,
    params,
    setAuction,
    setAuctionRequest,
    setDisableFileShare,
    setExternalId,
    setParams,
  ]);

  return context;
}
